import { VOICE_PARTS } from '@corocanto/scoring';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError } from '../api/client';
import { type EventDetail, type ItemKind, type ItemRequest, useEvent, useSetProgram } from '../api/events';
import { useSongs } from '../api/songs';
import type { Song } from '../api/types';
import { useMembership } from '../auth/session';
import { Dialog } from '../components/Dialog';
import { ErrorView, Loading } from '../components/Guards';
import { dateLocale } from '../i18n';
import { formatEventDates, formatLength, parseLength } from '../lib/eventDates';
import ui from '../styles/ui.module.css';
import styles from './RepertoirePage.module.css';

/** One programme item while it is being edited. Lengths of intermissions and speeches are typed as "m:ss". */
interface Draft {
  key: string;
  kind: ItemKind;
  songId: string | null;
  text: string;
  length: string;
  requested: boolean;
  deadline: string;
}

const NEW_DAYS = 14;
let nextKey = 0;
const draftKey = () => `item-${nextKey++}`;

function toDrafts(event: EventDetail): Draft[] {
  return event.program.map((item) => ({
    key: draftKey(),
    kind: item.kind,
    songId: item.songId,
    text: item.text ?? '',
    length: item.kind !== 'SONG' && item.durationSec !== null ? formatLength(item.durationSec) : '',
    requested: item.submissionRequested,
    deadline: item.submissionDeadline ?? '',
  }));
}

/** Director: an event's programme (wireframe 10). */
export function RepertoirePage() {
  const { eventId } = useParams();
  const membership = useMembership();
  const event = useEvent(membership?.choirId, eventId);
  const songs = useSongs(membership?.choirId);
  if (!membership) return null;
  if (event.isPending || songs.isPending) return <Loading />;
  if (event.isError || songs.isError || !event.data || !songs.data) return <ErrorView onRetry={() => void event.refetch()} />;
  return <Repertoire choirId={membership.choirId} event={event.data} library={songs.data} />;
}

function Repertoire({ choirId, event, library }: { choirId: string; event: EventDetail; library: Song[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSetProgram(choirId);
  const [items, setItems] = useState<Draft[]>(() => toDrafts(event));
  const [search, setSearch] = useState('');
  const [armed, setArmed] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [allOpen, setAllOpen] = useState(false);
  const [allDate, setAllDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const songsById = useMemo(() => new Map(library.map((s) => [s.id, s])), [library]);
  const locale = dateLocale();

  const songCount = items.filter((i) => i.kind === 'SONG').length;
  const total = items.reduce((sum, i) => {
    if (i.kind === 'SONG') return sum + (songsById.get(i.songId ?? '')?.summary.durationSeconds ?? 0);
    return sum + (parseLength(i.length) ?? 0);
  }, 0);
  const limitMinutes = event.type === 'COMPETITION' ? Number(event.details['timeLimitMinutes'] ?? 0) : 0;
  const over = limitMinutes > 0 ? Math.round(total) - limitMinutes * 60 : 0;

  const update = (key: string, patch: Partial<Draft>) => setItems((list) => list.map((i) => (i.key === key ? { ...i, ...patch } : i)));
  const move = (from: number, to: number) =>
    setItems((list) => {
      if (to < 0 || to >= list.length || from === to) return list;
      const next = [...list];
      const [item] = next.splice(from, 1);
      if (item) next.splice(to, 0, item);
      return next;
    });
  const add = (draft: Omit<Draft, 'key'>) => setItems((list) => [...list, { ...draft, key: draftKey() }]);

  const inProgram = new Set(items.map((i) => i.songId).filter(Boolean));
  const query = search.trim().toLocaleLowerCase(locale);
  const available = library.filter(
    (s) =>
      !inProgram.has(s.id) &&
      (!query || s.title.toLocaleLowerCase(locale).includes(query) || (s.composer ?? '').toLocaleLowerCase(locale).includes(query)),
  );

  async function onSave() {
    setError(null);
    const requests: ItemRequest[] = [];
    for (const item of items) {
      if (item.kind === 'SONG') {
        if (item.requested && !item.deadline) return setError(t('repertoire.needsDeadline'));
        requests.push({ kind: 'SONG', songId: item.songId, submissionRequested: item.requested, submissionDeadline: item.requested ? item.deadline : null });
        continue;
      }
      const length = item.length.trim() ? parseLength(item.length) : null;
      if (item.length.trim() && length === null) return setError(t('repertoire.invalidLength'));
      if (item.kind === 'SPEECH' && !item.text.trim()) return setError(t('repertoire.invalidSpeech'));
      requests.push({ kind: item.kind, text: item.kind === 'SPEECH' ? item.text.trim() : null, durationSec: length });
    }
    try {
      await save.mutateAsync({ eventId: event.id, items: requests });
      navigate(`/events/${event.id}`);
    } catch (e) {
      const code = e instanceof ApiError ? e.code : undefined;
      setError(t(`apiErrors.${code ?? 'unknown'}`, { defaultValue: t('apiErrors.unknown') }));
    }
  }

  return (
    <div className={`${ui.page} ${styles.page}`}>
      <div className={ui.pageHeader}>
        <div>
          <Link to={`/events/${event.id}`}>{event.name}</Link>
          <div className={styles.titleRow}>
            <h1>{t('repertoire.title')}</h1>
            <span className={`${ui.chip} ${ui.chipNeutral}`}>{t(`eventTypes.${event.type}`)}</span>
          </div>
          <div className={ui.subtitle}>
            {formatEventDates(event.startsOn, event.endsOn, locale)} · {t('repertoire.hint')}
          </div>
        </div>
        <div className={styles.headerButtons}>
          <Link to={`/events/${event.id}`} className={ui.button}>
            {t('repertoire.cancel')}
          </Link>
          <button type="button" className={`${ui.button} ${ui.primary}`} disabled={save.isPending || !event.readinessTracking} onClick={() => void onSave()}>
            {t('repertoire.save')}
          </button>
        </div>
      </div>

      {!event.readinessTracking && <div className={`${ui.notice} ${ui.noticeWarning}`}>{t('repertoire.noTracking')}</div>}
      {error && (
        <div className={`${ui.notice} ${ui.noticeError}`} role="alert">
          {error}
        </div>
      )}

      <div className={styles.layout}>
        <section className={ui.card} aria-label={t('repertoire.program', { count: songCount })} data-testid="program">
          <div className={styles.programHeader}>
            <h2 className={ui.cardTitle}>{t('repertoire.program', { count: songCount })}</h2>
            <span data-testid="program-total">
              {t('repertoire.total')} <strong>{formatLength(total)}</strong>
              {limitMinutes > 0 && <span className={ui.muted}> · {t('repertoire.timeLimit', { limit: formatLength(limitMinutes * 60) })}</span>}
            </span>
          </div>
          {over > 0 && (
            <div className={`${ui.notice} ${ui.noticeError}`} data-testid="over-limit">
              {t('repertoire.overLimit', { over: formatLength(over) })}
            </div>
          )}
          {items.length === 0 && <p className={ui.muted}>{t('repertoire.empty')}</p>}
          <ol className={styles.items}>
            {items.map((item, index) => {
              const number = items.slice(0, index + 1).filter((i) => i.kind === 'SONG').length;
              const song = item.songId ? songsById.get(item.songId) : undefined;
              return (
                <li
                  key={item.key}
                  className={`${styles.item} ${dragging === item.key ? styles.dragging : ''}`}
                  data-testid="program-item"
                  data-kind={item.kind}
                  draggable={armed === item.key}
                  onDragStart={(e) => {
                    setDragging(item.key);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragOver={(e) => {
                    if (!dragging) return;
                    e.preventDefault();
                    const from = items.findIndex((i) => i.key === dragging);
                    if (from !== index) move(from, index);
                  }}
                  onDragEnd={() => {
                    setDragging(null);
                    setArmed(null);
                  }}
                >
                  <span
                    className={styles.handle}
                    title={t('repertoire.hint')}
                    aria-hidden="true"
                    onMouseDown={() => setArmed(item.key)}
                    onMouseUp={() => setArmed(null)}
                  >
                    ≡
                  </span>
                  {item.kind === 'SONG' ? (
                    <>
                      <span className={styles.number}>{number}</span>
                      <div className={styles.what}>
                        <div className={styles.songTitle}>{song?.title ?? '–'}</div>
                        <div className={ui.muted}>{song ? songLine(song) : ''}</div>
                      </div>
                      <span className={styles.length}>{song ? formatLength(song.summary.durationSeconds) : ''}</span>
                      <label className={styles.deadline}>
                        <span className={ui.label}>{t('repertoire.deadline')}</span>
                        <input
                          type="date"
                          className={ui.input}
                          value={item.deadline}
                          disabled={!item.requested}
                          aria-label={`${t('repertoire.deadline')}: ${song?.title ?? ''}`}
                          onChange={(e) => update(item.key, { deadline: e.target.value })}
                        />
                      </label>
                      <label className={styles.request}>
                        <input
                          type="checkbox"
                          checked={item.requested}
                          aria-label={`${t('repertoire.request')}: ${song?.title ?? ''}`}
                          onChange={(e) => update(item.key, { requested: e.target.checked })}
                        />
                        {t('repertoire.request')}
                      </label>
                    </>
                  ) : (
                    <>
                      <span />
                      <div className={styles.what}>
                        {item.kind === 'INTERMISSION' ? (
                          <span className={styles.intermission}>{t('repertoire.intermission')}</span>
                        ) : (
                          <input
                            className={`${ui.input} ${styles.speechInput}`}
                            value={item.text}
                            maxLength={300}
                            aria-label={t('repertoire.speech')}
                            placeholder={t('repertoire.speechPlaceholder')}
                            onChange={(e) => update(item.key, { text: e.target.value })}
                          />
                        )}
                      </div>
                      <input
                        className={`${ui.input} ${styles.lengthInput}`}
                        value={item.length}
                        aria-label={item.kind === 'INTERMISSION' ? t('repertoire.intermissionLength') : t('repertoire.speechLength')}
                        onChange={(e) => update(item.key, { length: e.target.value })}
                      />
                      <span />
                      <span />
                    </>
                  )}
                  <div className={styles.moves}>
                    <button type="button" className={styles.iconButton} aria-label={t('repertoire.up')} disabled={index === 0} onClick={() => move(index, index - 1)}>
                      ↑
                    </button>
                    <button
                      type="button"
                      className={styles.iconButton}
                      aria-label={t('repertoire.down')}
                      disabled={index === items.length - 1}
                      onClick={() => move(index, index + 1)}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className={`${styles.iconButton} ${styles.remove}`}
                      aria-label={
                        item.kind === 'SONG' ? `${t('repertoire.remove')}: ${song?.title ?? ''}` : item.kind === 'INTERMISSION' ? t('repertoire.removeIntermission') : t('repertoire.removeSpeech')
                      }
                      onClick={() => setItems((list) => list.filter((i) => i.key !== item.key))}
                    >
                      ✕
                    </button>
                  </div>
                </li>
              );
            })}
          </ol>
          <div className={styles.addButtons}>
            <button type="button" className={ui.button} onClick={() => add({ kind: 'INTERMISSION', songId: null, text: '', length: '15:00', requested: false, deadline: '' })}>
              {t('repertoire.addIntermission')}
            </button>
            <button type="button" className={ui.button} onClick={() => add({ kind: 'SPEECH', songId: null, text: '', length: '5:00', requested: false, deadline: '' })}>
              {t('repertoire.addSpeech')}
            </button>
            <button type="button" className={ui.button} disabled={songCount === 0} onClick={() => setAllOpen(true)}>
              {t('repertoire.setAllDeadlines')}
            </button>
          </div>
          <div className={styles.keepNote}>{t('repertoire.keepNote')}</div>
        </section>

        <section className={`${ui.card} ${styles.library}`} data-testid="library-panel">
          <h2 className={ui.cardTitle}>{t('repertoire.library')}</h2>
          <label className={ui.field}>
            <span className={ui.label}>{t('repertoire.search')}</span>
            <input className={ui.input} type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
          </label>
          {available.length === 0 && <p className={ui.muted}>{t('repertoire.libraryEmpty')}</p>}
          <ul className={styles.libraryList}>
            {available.map((song) => (
              <li key={song.id}>
                <div className={styles.what}>
                  <div className={styles.songTitle}>{song.title}</div>
                  <div className={ui.muted}>{[song.composer, formatLength(song.summary.durationSeconds)].filter(Boolean).join(' · ')}</div>
                  {Date.now() - new Date(song.createdAt).getTime() < NEW_DAYS * 86_400_000 && (
                    <div className={styles.new}>{t('repertoire.newlyUploaded')}</div>
                  )}
                </div>
                <button
                  type="button"
                  className={ui.button}
                  aria-label={`${t('repertoire.add')}: ${song.title}`}
                  disabled={!event.readinessTracking}
                  onClick={() => add({ kind: 'SONG', songId: song.id, text: '', length: '', requested: false, deadline: '' })}
                >
                  {t('repertoire.add')}
                </button>
              </li>
            ))}
          </ul>
          <Link to="/library/upload" className={`${ui.button} ${styles.upload}`}>
            {t('repertoire.upload')}
          </Link>
        </section>
      </div>

      <Dialog open={allOpen} title={t('repertoire.setAllTitle')} onClose={() => setAllOpen(false)}>
        <p className={styles.noMargin}>{t('repertoire.setAllText')}</p>
        <label className={ui.field}>
          <span className={ui.label}>{t('repertoire.deadline')}</span>
          <input type="date" className={ui.input} value={allDate} onChange={(e) => setAllDate(e.target.value)} />
        </label>
        <div className={styles.dialogButtons}>
          <button type="button" className={ui.button} onClick={() => setAllOpen(false)}>
            {t('repertoire.cancel')}
          </button>
          <button
            type="button"
            className={`${ui.button} ${ui.primary}`}
            disabled={!allDate}
            onClick={() => {
              setItems((list) => list.map((i) => (i.kind === 'SONG' ? { ...i, requested: true, deadline: allDate } : i)));
              setAllOpen(false);
            }}
          >
            {t('repertoire.apply')}
          </button>
        </div>
      </Dialog>
    </div>
  );
}

/** "W. A. Mozart · S A T B · org": composer, voice parts, accompaniment instruments. */
function songLine(song: Song): string {
  const parts = VOICE_PARTS.filter((p) => song.summary.parts.some((x) => x.voicePart === p)).join(' ');
  const accompaniment = song.summary.parts
    .filter((p) => p.role === 'accompaniment')
    .map((p) => p.name.toLocaleLowerCase('tr'))
    .join(', ');
  return [song.composer, parts, accompaniment].filter(Boolean).join(' · ');
}
